import { useState, type FormEvent } from 'react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import type { ExceptionReason, TimetableSlot } from '@/types/timetable';
import { timetableApi } from '../../api/timetable.api';
import { formatInstituteDate } from '../../utils';
import { REASON_OPTIONS, WEEKDAY_LABEL, periodRange } from './timetableCopy';

/**
 * Saying whether one period on one date can be taken.
 *
 * Shared by the week grid and the day page, because both open exactly this
 * question and two copies would ask it two ways (CLAUDE.md §3).
 *
 * There is no third state: marking available *clears* the exception rather than
 * writing a row that means the opposite. That is also why the reason and note
 * disappear when Available is chosen — a row that says both would contradict
 * itself.
 */

export interface AvailabilityModalProps {
  slot: TimetableSlot;
  onClose: () => void;
  /** Fires after a successful save, so the caller can refresh its own view. */
  onSaved: () => void;
}

export function AvailabilityModal({ slot, onClose, onSaved }: AvailabilityModalProps) {
  const isAway = slot.availability.status === 'UNAVAILABLE';

  const [status, setStatus] = useState(slot.availability.status);
  const [reason, setReason] = useState<ExceptionReason>(slot.availability.reason ?? 'MEDICAL');
  const [note, setNote] = useState(slot.availability.note ?? '');
  const [isSaving, setIsSaving] = useState(false);

  async function save(event: FormEvent) {
    event.preventDefault();
    setIsSaving(true);

    try {
      await timetableApi.setAvailability(slot.uuid, {
        date: slot.date,
        status,
        ...(status === 'UNAVAILABLE' && { reason, ...(note.trim() && { note: note.trim() }) }),
      });

      toast.success(
        status === 'AVAILABLE'
          ? 'Marked as available again'
          : `Marked away for ${slot.subject.name}`,
        `${WEEKDAY_LABEL[slot.dayOfWeek]}, ${formatInstituteDate(slot.date)} · period ${slot.period.periodNumber}.`,
      );
      onSaved();
      onClose();
    } catch (caught) {
      // The server's messages name the reason — a past day, a period that is
      // not theirs — so they are worth showing verbatim.
      toast.error(
        'Could not save that',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={slot.subject.name}
      description={`${slot.displayName} · ${WEEKDAY_LABEL[slot.dayOfWeek]}, ${formatInstituteDate(slot.date)} · period ${slot.period.periodNumber}, ${periodRange(slot.period.startTime, slot.period.endTime)}`}
      size="sm"
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            isLoading={isSaving}
            form="availability-form"
            type="submit"
          >
            Save
          </Button>
        </>
      }
    >
      <form id="availability-form" onSubmit={save} className="flex flex-col gap-4">
        <Select
          label="Can you take this period?"
          options={[
            { value: 'AVAILABLE', label: 'Yes — I am taking it' },
            { value: 'UNAVAILABLE', label: 'No — I cannot take it' },
          ]}
          value={status}
          onChange={(event) => setStatus(event.target.value as typeof status)}
        />

        {status === 'UNAVAILABLE' ? (
          <>
            <Select
              label="Why"
              options={REASON_OPTIONS}
              value={reason}
              onChange={(event) => setReason(event.target.value as ExceptionReason)}
            />

            <Textarea
              label="Note for the office (optional)"
              rows={2}
              maxLength={255}
              placeholder="Anything the office needs to arrange cover…"
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />

            <Alert tone="info" title="The office sees this">
              It marks the period on your timetable so somebody can be found to cover it. It does
              not book leave for the whole day.
            </Alert>
          </>
        ) : (
          isAway && (
            <Alert tone="info" title="This clears the note you left">
              The period goes back to being yours, and the reason you gave is removed.
            </Alert>
          )
        )}
      </form>
    </Modal>
  );
}
